import { type FC, type ReactNode } from 'react'
import classNames from 'classnames'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { type IntegrationServiceId } from '../../../common/integrations'

export interface IntegrationOptionRowProps {
  serviceId?: IntegrationServiceId
  label: string
  detail: ReactNode
  selected: boolean
  onSelect: () => void
  /** Rendered under the row, inside the card. Kept outside the radio button so links in it stay reachable. */
  notice?: ReactNode
}

export const IntegrationOptionRow: FC<IntegrationOptionRowProps> = ({ serviceId, label, detail, selected, onSelect, notice }) => (
  <div
    className={classNames(
      'd-flex flex-column zocks-gap-0.75 p-3 rounded-3 border zocks-bg-color-white w-100',
      selected ? 'zocks-border-color-primary' : 'zocks-border-color-gray-300',
    )}
  >
    <button
      type='button'
      role='radio'
      aria-checked={selected}
      aria-label={label}
      onClick={onSelect}
      className='d-flex align-items-center gap-2 p-0 text-start border-0 bg-transparent w-100'
    >
      <i className={selected ? 'bi bi-record-circle-fill zocks-text-primary' : 'bi bi-circle zocks-text-gray-500'} />
      {serviceId ? <IntegrationIcon serviceId={serviceId} width='1.5rem' height='1.5rem' /> : null}
      <span className='d-flex flex-column overflow-hidden'>
        <span className='paragraph-small fw-bold zocks-text-gray-900 text-truncate'>{label}</span>
        <span className='paragraph-small'>{detail}</span>
      </span>
    </button>
    {notice}
  </div>
)

export const ConnectedDetail: FC<{ email?: string }> = ({ email }) => (
  <span className='d-flex align-items-center paragraph-small overflow-hidden'>
    <span className='d-flex align-items-center zocks-gap-0.25 zocks-text-success text-nowrap flex-shrink-0'>
      <i className='bi bi-check' />
      Connected
    </span>
    {email ? (
      <>
        <i className='bi bi-dot zocks-text-gray-600' />
        <span className='zocks-text-gray-600 text-truncate'>{email}</span>
      </>
    ) : null}
  </span>
)

export const ReconnectDetail: FC<{ email?: string; tooltip: string }> = ({ email, tooltip }) => (
  <span className='d-flex align-items-center paragraph-small overflow-hidden'>
    <Tooltip tooltipContent={tooltip} placement='bottom'>
      <span className='d-flex align-items-center zocks-gap-0.25 zocks-text-gray-600 text-nowrap flex-shrink-0'>
        <i className='bi bi-arrow-repeat' />
        Reconnect to use
      </span>
    </Tooltip>
    {email ? (
      <>
        <i className='bi bi-dot zocks-text-gray-600' />
        <span className='zocks-text-gray-600 text-truncate'>{email}</span>
      </>
    ) : null}
  </span>
)

export const SyncOffDetail: FC<{ tooltip: string }> = ({ tooltip }) => (
  <span className='d-flex align-items-center paragraph-small overflow-hidden'>
    <Tooltip tooltipContent={tooltip} placement='bottom'>
      <span className='d-flex align-items-center zocks-gap-0.25 zocks-text-gray-600 text-nowrap flex-shrink-0'>
        <i className='bi bi-toggle-off' />
        Turn on sync to use
      </span>
    </Tooltip>
  </span>
)
